import React, { useMemo, useState } from "react";
import { ChevronUp, ChevronDown, ArrowDownToLine, DownloadIcon } from "lucide-react";
import styles from "./VehicleMonitoringTable.module.css";
import TCUStyles from "../TCU.module.css"

/**
 * Status -> dot color mapping.
 * Expecting item.status to be one of: "critical" | "warning" | "ok"
 * (Adjust the keys to match whatever your API actually returns.)
 */
const STATUS_MAP = {
    critical: { color: "red", label: "Critical" },
    warning: { color: "orange", label: "Warning" },
    ok: { color: "green", label: "OK" },
};

const COLUMNS = [
    { key: "vehicle_number", label: "Vehicle Number" },
    { key: "registration_no", label: "Registration No" },
    { key: "depot", label: "Depot" },
    { key: "device_id", label: "Device ID" },
    { key: "avg_delay", label: "Avg Delay" },
    { key: "max_delay", label: "Max Delay" },
    { key: "issue_frequency", label: "Issue Frequency" },
    { key: "status", label: "Status" },
    { key: "last_comm", label: "Last Comm" },
    { key: "recommended_action", label: "Recommended Action", sortable: false },
];

const displayValue = (val) =>
    val === null || val === undefined || val === "" ? "-" : val;

/**
 * Hardcoded sample data (matches the reference screenshot).
 * Replace with API data via the `data` prop when ready.
 */
const SAMPLE_DATA = [
    { vehicle_number: "V-6006", registration_no: "KA51AJ006", depot: "Shantinagar", device_id: "DVC-9006", avg_delay: "67s", max_delay: "82s", issue_frequency: 1, status: "critical", last_comm: "14:53", recommended_action: "Inspect device / check time synchronization" },
    { vehicle_number: "V-6008", registration_no: "KA51AJ008", depot: "Shantinagar", device_id: "DVC-9008", avg_delay: "64s", max_delay: "78s", issue_frequency: 3, status: "critical", last_comm: "10:49", recommended_action: "Inspect device / check time synchronization" },
    { vehicle_number: "V-6009", registration_no: "KA51AJ009", depot: "Kannali", device_id: "DVC-9009", avg_delay: "82s", max_delay: "102s", issue_frequency: 15, status: "critical", last_comm: "14:18", recommended_action: "Inspect device / check time synchronization" },
    { vehicle_number: "V-6010", registration_no: "KA51AJ010", depot: "Kannali", device_id: "DVC-9010", avg_delay: "81s", max_delay: "103s", issue_frequency: 8, status: "critical", last_comm: "12:44", recommended_action: "Inspect device / check time synchronization" },
    { vehicle_number: "V-6001", registration_no: "DL51GD001", depot: "Mayapuri", device_id: "DVC-9001", avg_delay: "33s", max_delay: "47s", issue_frequency: 1, status: "warning", last_comm: "06:11", recommended_action: "Monitor device" },
    { vehicle_number: "V-6003", registration_no: "DL51GD003", depot: "Malvani Wet Lease", device_id: "DVC-9003", avg_delay: "51s", max_delay: "60s", issue_frequency: 17, status: "warning", last_comm: "10:06", recommended_action: "Monitor device" },
    { vehicle_number: "V-6004", registration_no: "DL51GD004", depot: "Malvani Wet Lease", device_id: "DVC-9004", avg_delay: "55s", max_delay: "70s", issue_frequency: 11, status: "warning", last_comm: "10:38", recommended_action: "Monitor device" },
    { vehicle_number: "V-6005", registration_no: "DL51GD005", depot: "Malvani Wet Lease", device_id: "DVC-9005", avg_delay: "41s", max_delay: "58s", issue_frequency: 7, status: "warning", last_comm: "06:43", recommended_action: "Monitor device" },
    { vehicle_number: "V-6007", registration_no: "KA51AJ007", depot: "Shantinagar", device_id: "DVC-9007", avg_delay: "57s", max_delay: "83s", issue_frequency: 15, status: "warning", last_comm: "10:15", recommended_action: "Monitor device" },
    { vehicle_number: "V-6002", registration_no: "DL51GD002", depot: "Mayapuri", device_id: "DVC-9002", avg_delay: "13s", max_delay: "22s", issue_frequency: 18, status: "ok", last_comm: "12:42", recommended_action: "No action needed" },
    { vehicle_number: "V-6011", registration_no: "MH02CX011", depot: "Andheri", device_id: "DVC-9011", avg_delay: "26s", max_delay: "38s", issue_frequency: 17, status: "ok", last_comm: "08:39", recommended_action: "No action needed" },
    { vehicle_number: "V-6012", registration_no: "MH02CX012", depot: "Andheri", device_id: "DVC-9012", avg_delay: "17s", max_delay: "35s", issue_frequency: 13, status: "ok", last_comm: "07:01", recommended_action: "No action needed" },
    { vehicle_number: "V-6013", registration_no: "MH02CX013", depot: "Andheri", device_id: "DVC-9013", avg_delay: "20s", max_delay: "34s", issue_frequency: 2, status: "ok", last_comm: "15:44", recommended_action: "No action needed" },
];

function StatusDot({ status }) {
    const meta = STATUS_MAP[status] || { color: "grey", label: displayValue(status) };
    return (
        <span className={styles.statusWrap}>
            <span className={`${styles.statusDot} ${styles[`dot_${meta.color}`]}`} />
        </span>
    );
}

function ChartLoader() {
    return (
        <div className={styles.loadingState}>
            <span className={styles.spinner} />
            <span>Loading...</span>
        </div>
    );
}

export default function VehicleMonitoringTable({
    data = SAMPLE_DATA,
    isLoading = false,
    onDownload,
    onVehicleClick,
    title = "Vehicle monitoring",
}) {
    const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

    const handleSort = (col) => {
        if (col.sortable === false) return;
        setSortConfig((prev) => {
            if (prev.key !== col.key) return { key: col.key, direction: "asc" };
            return {
                key: col.key,
                direction: prev.direction === "asc" ? "desc" : "asc",
            };
        });
    };

    const sortedData = useMemo(() => {
        if (!sortConfig.key) return data;
        const { key, direction } = sortConfig;

        return [...data].sort((a, b) => {
            const aVal = a?.[key];
            const bVal = b?.[key];

            if (aVal === undefined || aVal === null) return 1;
            if (bVal === undefined || bVal === null) return -1;

            if (typeof aVal === "number" && typeof bVal === "number") {
                return direction === "asc" ? aVal - bVal : bVal - aVal;
            }

            return direction === "asc"
                ? String(aVal).localeCompare(String(bVal))
                : String(bVal).localeCompare(String(aVal));
        });
    }, [data, sortConfig]);

    return (
        <div className={`card ${styles.card} mt-1 mx-3`}>
            <div className="d-flex justify-content-between align-items-center flex-wrap mb-2">
                        <div className={TCUStyles.titleWrap}>
                            <span className={TCUStyles.titleBar} />
                            <span className={TCUStyles.title}>{title}</span>
                            {/* <span className={TCUStyles.subtitle}>{subtitle}</span> */}
                        </div>
                        <span className="custom-Btn-animation" /* onClick={downloadVehicleData} */><DownloadIcon size={15} /></span>
                    </div>

            <div className={`table-responsive ${styles.tableWrapper}`}>
                <table
                    className={`table table-bordered table-hover align-middle mb-0 ${styles.mainTable}`}
                >
                    <thead className="sticky-top">
                        <tr>
                            {COLUMNS.map((col) => {
                                const isActive = sortConfig.key === col.key;
                                return (
                                    <th
                                        key={col.key}
                                        onClick={() => handleSort(col)}
                                        className={col.sortable === false ? "" : styles.sortableTh}
                                    >
                                        <span className={styles.thContent}>
                                            {col.label}
                                            {col.sortable !== false && (
                                                <span className={styles.sortIcon}>
                                                    {isActive && sortConfig.direction === "asc" ? (
                                                        <ChevronUp size={12} />
                                                    ) : (
                                                        <ChevronDown size={12} />
                                                    )}
                                                </span>
                                            )}
                                        </span>
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>

                    <tbody>
                        {isLoading ? (
                            <tr>
                                <td colSpan={COLUMNS.length}>
                                    <ChartLoader />
                                </td>
                            </tr>
                        ) : sortedData?.length ? (
                            sortedData.map((item, idx) => (
                                <tr key={item.vehicle_number || idx} className={styles.dataRow}>
                                    <td
                                        className={`${styles.vehicleCell} cursor-pointer`}
                                        onClick={() => onVehicleClick?.(item)}
                                    >
                                        {displayValue(item.vehicle_number)}
                                    </td>
                                    <td>{displayValue(item.registration_no)}</td>
                                    <td>{displayValue(item.depot)}</td>
                                    <td>{displayValue(item.device_id)}</td>
                                    <td>{displayValue(item.avg_delay)}</td>
                                    <td>{displayValue(item.max_delay)}</td>
                                    <td>{displayValue(item.issue_frequency)}</td>
                                    <td>
                                        <StatusDot status={item.status} />
                                    </td>
                                    <td>{displayValue(item.last_comm)}</td>
                                    <td className={styles.actionCell}>
                                        {displayValue(item.recommended_action)}
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={COLUMNS.length}>
                                    <div className="v-title">
                                        <div className="alert alert-danger no_data">
                                            Records not found
                                        </div>
                                    </div>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}